import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer } from '@react-google-maps/api';
import { useLocation } from '../store';
import { getNearbyDhabas, getDhabasAlongRoute } from '../services/dhabaService';

const MAP_CONTAINER = { width: '100%', height: '100vh' };

const CATEGORIES = [
  { id: 'food', label: 'Food', icon: '🍽️', placesType: 'restaurant' },
  { id: 'fuel', label: 'Fuel', icon: '⛽', placesType: 'gas_station' },
  { id: 'stay', label: 'Stay', icon: '🏨', placesType: 'lodging' },
  { id: 'ev', label: 'EV', icon: '🔌', placesType: 'electric_vehicle_charging_station' },
];

const MARKER_COLORS = {
  food: '#D4AF37',
  fuel: '#EF4444',
  stay: '#8B5CF6',
  ev: '#22C55E',
};

function createSVGMarker(emoji, color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
    <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26s18-12.5 18-26C36 8.06 27.94 0 18 0z" fill="${color}"/>
    <circle cx="18" cy="16" r="11" fill="white"/>
    <text x="18" y="21" text-anchor="middle" font-size="14">${emoji}</text>
  </svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

const CURRENT_LOC_ICON = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#4285F4" opacity="0.2"/>
    <circle cx="12" cy="12" r="7" fill="#4285F4"/>
    <circle cx="12" cy="12" r="3" fill="white"/>
  </svg>
`);

export function MapScreen({ onBack, onSelectDhaba, onPreOrder }) {
  const { coords, locationName } = useLocation();
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [activeFilters, setActiveFilters] = useState({ food: true, fuel: false, stay: false, ev: false });
  const [mapType, setMapType] = useState('roadmap');

  // Route state
  const [destination, setDestination] = useState('');
  const [destinationCoords, setDestinationCoords] = useState(null);
  const [directions, setDirections] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [isSettingRoute, setIsSettingRoute] = useState(false);
  const [routeActive, setRouteActive] = useState(false);

  // Markers state
  const [dhabas, setDhabas] = useState([]);
  const [placesResults, setPlacesResults] = useState({});
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchPredictions, setSearchPredictions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const inputRef = useRef(null);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    if (window.google?.maps?.places) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstance);
    }
  }, []);

  // Fetch nearby dhabas on load (within reasonable radius)
  useEffect(() => {
    if (!coords) return;
    (async () => {
      try {
        const data = await getNearbyDhabas(coords, 100);
        setDhabas(data.map(d => ({
          ...d, markerType: 'food',
          lat: d.location?.coordinates?.[1] || coords.lat,
          lng: d.location?.coordinates?.[0] || coords.lng,
        })));
      } catch (err) { console.error(err); }
    })();
  }, [coords]);

  // Search destination using Nominatim
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchPredictions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&countrycodes=in&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        if (res.ok) {
          const data = await res.json();
          const seen = new Set();
          setSearchPredictions(data.filter(item => {
            const name = (item.address?.city || item.address?.town || item.display_name.split(',')[0]).toLowerCase();
            if (seen.has(name)) return false;
            seen.add(name);
            return true;
          }).map(item => ({
            id: item.place_id,
            name: item.address?.city || item.address?.town || item.address?.village || item.display_name.split(',')[0],
            state: item.address?.state || '',
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
          })));
        }
      } catch (e) { console.warn(e); }
      setSearchLoading(false);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  // Calculate route when destination is set
  const calculateRoute = useCallback((destCoords) => {
    if (!coords || !destCoords || !window.google?.maps) return;

    setLoading(true);
    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: new window.google.maps.LatLng(coords.lat, coords.lng),
        destination: new window.google.maps.LatLng(destCoords.lat, destCoords.lng),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result);
          const leg = result.routes[0].legs[0];
          setRouteInfo({ distance: leg.distance.text, duration: leg.duration.text });
          setRouteActive(true);

          // Fetch dhabas along the route from backend
          getDhabasAlongRoute(coords, destCoords).then(routeDhabas => {
            setDhabas(routeDhabas.map(d => ({
              ...d, markerType: 'food',
              lat: d.location?.coordinates?.[1],
              lng: d.location?.coordinates?.[0],
            })));
          }).catch(console.error);

          // Search Google Places along route for active filters
          searchAlongRoute(result.routes[0]);
        } else {
          console.error('Directions failed:', status);
          alert('Could not find a route. Please try a different destination.');
        }
        setLoading(false);
      }
    );
  }, [coords]);

  // Search Google Places along the route path
  const searchAlongRoute = useCallback((route) => {
    if (!placesServiceRef.current || !route?.overview_path) return;

    const path = route.overview_path;
    // Sample points along route (every ~50km)
    const totalPoints = path.length;
    const step = Math.max(1, Math.floor(totalPoints / 8));
    const samplePoints = [];
    for (let i = 0; i < totalPoints; i += step) {
      samplePoints.push(path[i]);
    }

    CATEGORIES.filter(c => c.id !== 'food' && c.placesType).forEach(cat => {
      const allResults = [];
      let completed = 0;

      samplePoints.forEach(point => {
        placesServiceRef.current.nearbySearch(
          { location: point, radius: 5000, type: cat.placesType },
          (results, status) => {
            completed++;
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              results.forEach(p => {
                if (!allResults.find(r => r.id === p.place_id)) {
                  allResults.push({
                    id: p.place_id, name: p.name, markerType: cat.id,
                    lat: p.geometry.location.lat(), lng: p.geometry.location.lng(),
                    rating: p.rating, vicinity: p.vicinity,
                    isGooglePlace: true,
                  });
                }
              });
            }
            if (completed === samplePoints.length) {
              setPlacesResults(prev => ({ ...prev, [cat.id]: allResults }));
            }
          }
        );
      });
    });
  }, []);

  // Select destination
  const handleSelectDestination = (pred) => {
    setDestination(pred.name);
    setDestinationCoords({ lat: pred.lat, lng: pred.lng });
    setSearchQuery('');
    setSearchPredictions([]);
    setIsSettingRoute(false);
    calculateRoute({ lat: pred.lat, lng: pred.lng });
  };

  // Clear route
  const clearRoute = () => {
    setDirections(null);
    setRouteInfo(null);
    setRouteActive(false);
    setDestination('');
    setDestinationCoords(null);
    setPlacesResults({});
    // Re-fetch nearby dhabas
    if (coords) {
      getNearbyDhabas(coords, 100).then(data => {
        setDhabas(data.map(d => ({
          ...d, markerType: 'food',
          lat: d.location?.coordinates?.[1] || coords.lat,
          lng: d.location?.coordinates?.[0] || coords.lng,
        })));
      }).catch(console.error);
    }
  };

  // Toggle filter
  const toggleFilter = (filterId) => {
    setActiveFilters(prev => ({ ...prev, [filterId]: !prev[filterId] }));
  };

  // Get all visible markers
  const getVisibleMarkers = () => {
    let markers = [];
    if (activeFilters.food) markers = [...markers, ...dhabas];
    Object.entries(placesResults).forEach(([catId, results]) => {
      if (activeFilters[catId]) markers = [...markers, ...results];
    });
    return markers;
  };

  const visibleMarkers = getVisibleMarkers();

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER}
        center={coords || { lat: 28.6139, lng: 77.2090 }}
        zoom={13}
        onLoad={onMapLoad}
        mapTypeId={mapType}
        options={{
          zoomControl: false, streetViewControl: false,
          mapTypeControl: false, fullscreenControl: false,
          styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        }}
      >
        {/* Current location */}
        {coords && (
          <Marker
            position={coords}
            icon={{ url: CURRENT_LOC_ICON, scaledSize: new window.google.maps.Size(24, 24) }}
            zIndex={1000}
          />
        )}

        {/* Route */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { strokeColor: '#D4AF37', strokeWeight: 5, strokeOpacity: 0.9 },
            }}
          />
        )}

        {/* Place markers */}
        {visibleMarkers.map((marker, idx) => (
          <Marker
            key={marker.id || `m-${idx}`}
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => setSelectedMarker(marker)}
            icon={{
              url: createSVGMarker(
                CATEGORIES.find(c => c.id === marker.markerType)?.icon || '🍽️',
                MARKER_COLORS[marker.markerType] || '#D4AF37'
              ),
              scaledSize: new window.google.maps.Size(36, 44),
              anchor: new window.google.maps.Point(18, 44),
            }}
          />
        ))}

        {/* Info window */}
        {selectedMarker && (
          <InfoWindow
            position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
            onCloseClick={() => setSelectedMarker(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -44) }}
          >
            <div style={{ padding: '4px', minWidth: '180px', maxWidth: '240px' }}>
              <h3 style={{ fontWeight: '700', fontSize: '15px', color: '#1A1A1A', marginBottom: '4px', fontFamily: "'Manrope', sans-serif" }}>
                {selectedMarker.name}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ padding: '2px 8px', borderRadius: '12px', background: MARKER_COLORS[selectedMarker.markerType] || '#D4AF37', color: '#fff', fontSize: '11px', fontWeight: '600' }}>
                  {CATEGORIES.find(c => c.id === selectedMarker.markerType)?.label || 'Place'}
                </span>
                {selectedMarker.rating > 0 && (
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A' }}>⭐ {selectedMarker.rating}</span>
                )}
              </div>
              {/* HT Certification Badges */}
              {(selectedMarker.ht_certified_dhaba || selectedMarker.ht_certified_dining || selectedMarker.ht_certified_washroom) && (
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                  {selectedMarker.ht_certified_dhaba && (
                    <img src="/images/badges/ht-certified-dhaba.png" alt="HT Certified Dhaba" style={{ height: '24px', objectFit: 'contain' }} />
                  )}
                  {selectedMarker.ht_certified_dining && !selectedMarker.ht_certified_dhaba && (
                    <img src="/images/badges/ht-certified-dining.png" alt="HT Certified Dining" style={{ height: '24px', objectFit: 'contain' }} />
                  )}
                  {selectedMarker.ht_certified_washroom && !selectedMarker.ht_certified_dhaba && (
                    <img src="/images/badges/ht-certified-washroom.png" alt="HT Certified Washroom" style={{ height: '24px', objectFit: 'contain' }} />
                  )}
                </div>
              )}
              {selectedMarker.cuisine && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                  {Array.isArray(selectedMarker.cuisine) ? selectedMarker.cuisine.join(' · ') : selectedMarker.cuisine}
                </p>
              )}
              {selectedMarker.vicinity && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{selectedMarker.vicinity}</p>
              )}
              {selectedMarker.distance && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>{selectedMarker.distance} · {selectedMarker.time}</p>
              )}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {selectedMarker.markerType === 'food' && !selectedMarker.isGooglePlace && (
                  <>
                    <button onClick={() => {
                        // Calculate ETA from distance string (e.g., "35.0 km" → ~52 min)
                        const distKm = parseFloat(selectedMarker.distance) || 30;
                        const etaMins = Math.round((distKm / 60) * 60) || 30;
                        onPreOrder && onPreOrder(selectedMarker, etaMins);
                      }} data-testid="map-preorder-btn"
                      style={{ flex: 1, padding: '8px 12px', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                      Pre-Order
                    </button>
                    <button onClick={() => onSelectDhaba && onSelectDhaba(selectedMarker)} data-testid="map-view-details-btn"
                      style={{ flex: 1, padding: '8px 12px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      Details
                    </button>
                  </>
                )}
                <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedMarker.lat},${selectedMarker.lng}`, '_blank')}
                  data-testid="map-directions-btn"
                  style={{ flex: 1, padding: '8px 12px', background: '#1A1A1A', color: '#D4AF37', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                  Directions
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Back button */}
      <button onClick={onBack} data-testid="map-back-btn"
        style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 10, width: '44px', height: '44px', borderRadius: '14px', background: '#FFF', border: 'none', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
        ←
      </button>

      {/* Map controls (right side) */}
      <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <button onClick={() => setMapType(prev => prev === 'roadmap' ? 'satellite' : 'roadmap')} data-testid="map-type-toggle"
          style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#FFF', border: 'none', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', fontSize: '16px' }}>
          {mapType === 'roadmap' ? '🛰️' : '🗺️'}
        </button>
        <button onClick={() => { if (map && coords) { map.panTo(coords); map.setZoom(14); } }} data-testid="map-recenter-btn"
          style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#FFF', border: 'none', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', fontSize: '18px' }}>
          ◎
        </button>
      </div>

      {/* Route input panel */}
      {isSettingRoute ? (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50, background: '#FFF' }}>
          {/* Header */}
          <div style={{ padding: '16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <button onClick={() => setIsSettingRoute(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>←</button>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1A1A1A', fontFamily: "'Manrope', sans-serif" }}>Set Your Route</h2>
            </div>

            {/* From */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#4285F4', flexShrink: 0 }} />
              <div style={{ flex: 1, padding: '12px 14px', background: '#F3F4F6', borderRadius: '12px', fontSize: '14px', color: '#374151' }}>
                {locationName || 'Current Location'}
              </div>
            </div>

            {/* To */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: '#D4AF37', flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Where are you going?"
                autoFocus
                data-testid="route-destination-input"
                style={{ flex: 1, padding: '12px 14px', background: '#F3F4F6', borderRadius: '12px', fontSize: '14px', border: '2px solid #D4AF37', outline: 'none' }}
              />
            </div>
          </div>

          {/* Search results */}
          <div style={{ padding: '8px 16px', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
            {searchLoading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ width: '24px', height: '24px', border: '3px solid #E5E7EB', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
              </div>
            )}
            {searchPredictions.map(pred => (
              <button key={pred.id} onClick={() => handleSelectDestination(pred)} data-testid={`route-dest-${pred.id}`}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 8px', background: 'transparent', border: 'none', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', textAlign: 'left' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#D4AF37"/>
                  <circle cx="12" cy="9" r="2.5" fill="#fff"/>
                </svg>
                <div>
                  <p style={{ fontWeight: '600', fontSize: '15px', color: '#1A1A1A' }}>{pred.name}</p>
                  {pred.state && <p style={{ fontSize: '13px', color: '#6B7280' }}>{pred.state}</p>}
                </div>
              </button>
            ))}
            {!searchLoading && !searchQuery && (
              <p style={{ color: '#9CA3AF', fontSize: '14px', padding: '20px 0', textAlign: 'center' }}>
                Type a city or destination to plan your route
              </p>
            )}
          </div>
        </div>
      ) : (
        /* Route bar / Set destination button */
        <div style={{ position: 'absolute', top: '16px', left: '68px', right: '68px', zIndex: 10 }}>
          {routeActive && routeInfo ? (
            /* Active route info */
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '12px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#1A1A1A' }}>{destination}</span>
                <button onClick={clearRoute} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#EF4444' }}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <span style={{ fontSize: '12px', color: '#D4AF37', fontWeight: '600' }}>🕐 {routeInfo.duration}</span>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>📍 {routeInfo.distance}</span>
                <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: '600' }}>🍽️ {dhabas.length} dhabas</span>
              </div>
            </div>
          ) : (
            /* Set destination button */
            <button onClick={() => setIsSettingRoute(true)} data-testid="set-route-btn"
              style={{ width: '100%', padding: '12px 16px', background: '#FFF', borderRadius: '14px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>🔍</span>
              <span style={{ fontSize: '14px', color: '#9CA3AF' }}>Where are you going?</span>
            </button>
          )}
        </div>
      )}

      {/* Category filter chips (below route bar) */}
      {!isSettingRoute && (
        <div style={{ position: 'absolute', top: routeActive ? '90px' : '70px', left: '16px', right: '16px', zIndex: 10, display: 'flex', gap: '6px', overflowX: 'auto' }}>
          {CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => toggleFilter(cat.id)} data-testid={`map-filter-${cat.id}`}
              style={{
                padding: '8px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                background: activeFilters[cat.id] ? 'linear-gradient(135deg, #1A1A1A, #2D2D2D)' : '#FFFFFF',
                color: activeFilters[cat.id] ? '#D4AF37' : '#374151',
                fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px',
              }}>
              <span style={{ fontSize: '14px' }}>{cat.icon}</span>{cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Bottom info bar */}
      {!isSettingRoute && (
        <div style={{
          position: 'absolute', bottom: '24px', left: '16px', right: '16px', zIndex: 10,
          background: 'linear-gradient(135deg, #1A1A1A, #2D2D2D)', borderRadius: '16px',
          padding: '14px 20px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          maxWidth: '28rem', margin: '0 auto',
        }}>
          <div>
            <p style={{ color: '#D4AF37', fontWeight: '700', fontSize: '14px', fontFamily: "'Manrope', sans-serif" }}>
              {loading ? 'Loading...' : `${visibleMarkers.length} places found`}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
              {routeActive ? `On route to ${destination}` : 'Nearby'} · Tap markers for details
            </p>
          </div>
          {!routeActive && (
            <button onClick={() => setIsSettingRoute(true)}
              style={{ padding: '8px 14px', borderRadius: '10px', background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              Set Route
            </button>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default MapScreen;
